/**
 * AutoUnlockCard — platform-admin approval of automatic mode for a workspace.
 *
 * In automatic mode the optimization engine acts on campaigns (moves budgets,
 * pauses ads, requests billed AI challengers) without approval in the Action
 * Inbox. The other way to unlock it is a clean history of approved engine
 * actions, which a first live campaign cannot have. So an admin may approve
 * it directly — but only as a deliberate act over the guardrails shown here:
 * the confirm checkbox and the note are required, and the server audits both.
 *
 * Rendered only for platform admins (the server route is requireAdmin too).
 */

import { useState } from 'react';
import type { AdoptGuardrailsDto } from '@/lib/api';
import {
  useAdoptAutoUnlockReview,
  useGrantAdoptAutoUnlock,
  useRevokeAdoptAutoUnlock,
} from '../hooks/useAdvertisingOptimization';

const NOTE_MAX_CHARS = 500;

function usd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** Credits are cents: "250 credits ($2.50)". */
function credits(n: number): string {
  return `${n.toLocaleString()} credits (${usd(n)})`;
}

function GuardrailRows({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function StrategyRows({ strategy }: { strategy: AdoptGuardrailsDto['defaultStrategy'] }) {
  const rows: Array<[string, string]> = [
    ['Live creative variants (max)', String(strategy.explorationMaxVariantsLive)],
    ['Spend before a variant is judged', usd(strategy.explorationMinSpendPerVariantCents)],
    ['Minimum observation', `${strategy.minObservationHours}h`],
    [
      'Daily budget change (max)',
      `${strategy.maxDailyBudgetDeltaPct}% and ${usd(strategy.maxDailyBudgetDeltaCents)} (both apply)`,
    ],
    ['Budget scale step', `${strategy.scaleStepPct}%`],
    ['Cooldown after a budget change', `${strategy.cooldownHoursAfterBudgetChange}h`],
    ['Promote / kill confidence', `${strategy.promoteConfidence} / ${strategy.killConfidence}`],
    ['Challengers per request', String(strategy.challengerBatchSize)],
  ];
  return <GuardrailRows rows={rows} />;
}

/**
 * Guest trials an ad click can start. All three numbers come from the server
 * (the strategy defaults and the platform ceilings the mint clamps to), so this
 * can never show a bound the mint does not enforce.
 */
function TrialRows({ guardrails }: { guardrails: AdoptGuardrailsDto }) {
  const { defaultStrategy, trial } = guardrails;
  return (
    <GuardrailRows
      rows={[
        [
          'Guest trials per campaign a day',
          `${defaultStrategy.maxTrialsPerDay} by default (a campaign may set up to ${trial.maxTrialsPerDayLimit})`,
        ],
        [
          'Trial credits per guest',
          `${credits(trial.defaultCredits)} by default; a campaign may set up to ${credits(trial.maxCampaignCredits)}`,
        ],
      ]}
    />
  );
}

/** "250 credits × up to 15 a day", plus a suspension the sweep set. */
function campaignTrialSummary(
  strategy: AdoptGuardrailsDto['campaigns'][number]['strategy'],
  trial: AdoptGuardrailsDto['trial'],
): string {
  const amount = strategy.trialCreditAmount ?? trial.defaultCredits;
  const summary = `guest trials ${amount} credits × up to ${strategy.maxTrialsPerDay} a day`;
  return strategy.trialsSuspended ? `${summary} (suspended)` : summary;
}

export function AutoUnlockCard({ holderId }: { holderId: string }) {
  const review = useAdoptAutoUnlockReview(holderId);
  const grant = useGrantAdoptAutoUnlock(holderId);
  const revoke = useRevokeAdoptAutoUnlock(holderId);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  if (review.isLoading) {
    return <section className="text-sm text-muted-foreground">Loading automatic mode…</section>;
  }
  if (!review.data) return null;
  const { autoUnlock, guardrails } = review.data;
  const canApprove = confirmed && note.trim().length > 0 && !grant.isPending;

  return (
    <section
      aria-labelledby="adopt-auto-unlock-title"
      className="rounded-lg border border-border p-4 flex flex-col gap-4"
    >
      <div>
        <h2 id="adopt-auto-unlock-title" className="font-medium">
          Unlock automatic mode
        </h2>
        <p className="text-sm text-muted-foreground">
          Platform admins only. In automatic mode the optimization engine acts on this
          workspace&apos;s campaigns without approval in the Action Inbox, within the guardrails
          below.
        </p>
      </div>

      <div className="text-sm">
        {autoUnlock.via === 'admin' && autoUnlock.adminUnlock ? (
          <div className="flex flex-col gap-2">
            <span className="text-emerald-700">
              Unlocked by an administrator on{' '}
              {new Date(autoUnlock.adminUnlock.unlockedAt).toLocaleString()}.
            </span>
            <span className="text-muted-foreground">Note: {autoUnlock.adminUnlock.note}</span>
            <div>
              <button
                onClick={() => revoke.mutate()}
                disabled={revoke.isPending}
                className="px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 text-sm disabled:opacity-50"
              >
                Revoke approval
              </button>
            </div>
            <span className="text-xs text-muted-foreground">
              Revoking stops campaigns being switched to automatic; campaigns already in automatic
              mode stay there until you change them.
            </span>
          </div>
        ) : autoUnlock.via === 'history' ? (
          <span className="text-emerald-700">
            Unlocked by a clean history ({autoUnlock.executed} approved engine actions executed in{' '}
            {autoUnlock.windowDays} days).
          </span>
        ) : (
          <span className="text-muted-foreground">
            Locked: {autoUnlock.executed}/{autoUnlock.required} clean engine executions in{' '}
            {autoUnlock.windowDays} days
            {autoUnlock.failed > 0 ? ` (${autoUnlock.failed} failed)` : ''}.
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Default guardrails for every campaign</h3>
        <StrategyRows strategy={guardrails.defaultStrategy} />
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Guest trials from ad clicks</h3>
        <TrialRows guardrails={guardrails} />
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Campaign ceilings</h3>
        {guardrails.campaigns.length === 0 ? (
          <p className="text-sm text-muted-foreground">No campaigns yet.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {guardrails.campaigns.map((c) => (
              <li key={c.id}>
                <span className="font-medium">{c.name}</span>{' '}
                <span className="text-muted-foreground">
                  ({c.status}, {c.autonomyLevel}) — daily ceiling {usd(c.dailyBudgetCeilingCents)},
                  total {usd(c.totalBudgetCents)}, up to {c.strategy.explorationMaxVariantsLive}{' '}
                  live variants, budget moves ≤ {c.strategy.maxDailyBudgetDeltaPct}% /{' '}
                  {usd(c.strategy.maxDailyBudgetDeltaCents)} a day,{' '}
                  {campaignTrialSummary(c.strategy, guardrails.trial)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {autoUnlock.via !== 'admin' && (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Approval note (what you reviewed)</span>
            <textarea
              rows={2}
              maxLength={NOTE_MAX_CHARS}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              I have reviewed these guardrails and approve automatic mode for this workspace.
            </span>
          </label>
          <div>
            <button
              onClick={() => grant.mutate(note.trim())}
              disabled={!canApprove}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              Unlock automatic mode
            </button>
          </div>
        </div>
      )}

      {(grant.isError || revoke.isError) && (
        <div className="text-sm text-rose-600">
          {((grant.error ?? revoke.error) as Error | null)?.message ?? 'Action failed'}
        </div>
      )}
    </section>
  );
}
